// Dependencies
import React from 'react';
import radium from 'radium';
import {ThemeContext} from '../../components/theme/Theme.jsx';
import {API_URL} from "../../dev";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

function Image(props) {
    const context = React.useContext(ThemeContext);
    const [loadError, setLoadError] = React.useState(false);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [showDelayedFallback, setShowDelayedFallback] = React.useState(false);
    const style = {
        container: {
            float: 'left',
            margin: context.themeManager.value('event-image', 'margin'),
            flexShrink: '0',
            paddingBottom: '1.5em',
            width: '100%',
            '@media (min-width : 768px)': {
                width: '40%',
                paddingBottom: '0',
            },
        },
        figure: {
            width: '100%',
            padding: '0 0 0.5em 0',
            '@media (min-width : 768px)': {
                padding: '0 1.5em 0.2em 0',
            },
        },
        image: {
            width: '100%',
        },
        fallback: {
            width: '100%',
            aspectRatio: '16/9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '2px solid ' + context.themeManager.value('event-default-image', 'borderColor', 'fg-color-text-disabled'),
            borderRadius: '5px',
            color: context.themeManager.value('event-default-image', 'color', 'fg-color-text-disabled'),
        },
        caption: {
            fontSize: '0.9em',
            wordBreak: 'break-all',
        },
        source: {
            fontSize: '0.8em',
            fontWeight: 300,
            wordBreak: 'break-all',
        },
    };

    const meta = props.imageMetaData;
    const hasMetaId = Boolean(meta && meta.id);
    const showFallback = !hasMetaId || loadError || (showDelayedFallback && !isLoaded);

    // Show the fallback only after 500 ms if the image is still loading.
    // As soon as the image has loaded, immediately switch to the real image.
    React.useEffect(() => {
        // Reset states when the metadata ID changes
        setLoadError(false);
        setIsLoaded(false);
        setShowDelayedFallback(false);

        if (!hasMetaId) {
            // No image available → show fallback immediately (no delay)
            return;
        }

        const src = `${API_URL}/v1/files/scaled/IMG600/${meta.id}`;

        let done = false;
        const timer = setTimeout(() => {
            if (!done) setShowDelayedFallback(true);
        }, 500);

        // Explicitly use the global Image constructor (not the React component)
        const preloader = new window.Image();
        preloader.onload = () => {
            done = true;
            clearTimeout(timer);
            setIsLoaded(true);
        };
        preloader.onerror = () => {
            done = true;
            clearTimeout(timer);
            setLoadError(true);
        };
        preloader.src = src;

        return () => {
            done = true;
            clearTimeout(timer);
            preloader.onload = null;
            preloader.onerror = null;
        };
    }, [meta?.id, hasMetaId]);


    return (
        <div className="event__image-container" style={style.container}>
            <figure style={style.figure}>
                {!showFallback ? (
                    <img
                        src={`${API_URL}/v1/files/scaled/IMG600/${meta.id}`}
                        alt={meta?.altText}
                        className="event__image"
                        style={style.image}
                        onError={(e) => {
                            // Prevent infinite loops; mark error so the fallback is shown
                            e.currentTarget.onerror = null;
                            setLoadError(true);
                        }}
                    />
                ) : (
                    <div style={style.fallback} aria-label="Das Veranstaltungsbild konnte nicht geladen werden."
                         title="Das Veranstaltungsbild konnte nicht geladen werden.">
                        <FontAwesomeIcon icon="image" size="6x" color={style.fallback.color}/>
                    </div>
                )}

                {!showFallback && (
                    <>
                        {meta?.caption && (
                            <figcaption style={style.caption}>{meta.caption}</figcaption>
                        )}
                        {meta?.source && (
                            <figcaption style={style.source}>{`Quelle: ${meta.source}`}</figcaption>
                        )}
                    </>
                )}
            </figure>
        </div>
    );
}

export default radium(Image);
